import { EMPTY_UUID } from "../../../constants/appConstants";

export const parseSpecialtyCategoryDetailResponse = (res) => {
    if (!res || typeof res !== "object") return { ok: false, payload: null };
    const ok = res.isSuccess ?? res.IsSuccess;
    const payload = res.data ?? res.Data;
    if (payload && (ok === undefined || ok === true)) return { ok: true, payload };
    if (payload && ok === false) return { ok: false, payload };
    return { ok: false, payload: null };
};

const uniqueById = (items) => {
    const seen = new Set();
    const out = [];
    (Array.isArray(items) ? items : []).forEach((item) => {
        const id = String(item?.id ?? item?.Id ?? "");
        if (!id || seen.has(id.toLowerCase())) return;
        seen.add(id.toLowerCase());
        out.push(item);
    });
    return out;
};

export const getAttributeList = (detail) => {
    const list =
        detail?.specialtyAttributeList ??
        detail?.SpecialtyAttributeList ??
        detail?.specialtyCategoryAttributeList ??
        detail?.SpecialtyCategoryAttributeList;
    if (Array.isArray(list) && list.length) return list;

    const byTitle =
        detail?.filters?.attributesByTitleId ??
        detail?.Filters?.AttributesByTitleId;
    if (byTitle && typeof byTitle === "object") {
        const merged = [];
        Object.values(byTitle).forEach((group) => {
            const attrs = group?.specialtyAttributeList ?? group?.SpecialtyAttributeList ?? [];
            merged.push(...attrs);
        });
        const unique = uniqueById(merged);
        if (unique.length) return unique;
    }
    return Array.isArray(list) ? list : [];
};

export const getItemIdFromAttrItem = (it) => it?.id ?? it?.Id;

export const getAttributeItemList = (attr) =>
    attr?.specialtyAttributeItemList ??
    attr?.SpecialtyAttributeItemList ??
    attr?.specialtyCategoryAttributeItemList ??
    attr?.SpecialtyCategoryAttributeItemList ??
    [];

export const getAttributeKind = (attr) =>
    attr?.kind ?? attr?.Kind ?? attr?.attributeKind ?? attr?.AttributeKind ?? "";

/** Kind önceliği: SubSpecialty → TreatmentMethod → PracticeArea → diğerleri */
const KIND_SORT_ORDER = {
    SubSpecialty: 0,
    TreatmentMethod: 1,
    PracticeArea: 2,
};

export const sortAttributesByKind = (list) => {
    if (!Array.isArray(list) || list.length <= 1) return list || [];
    return [...list].sort((a, b) => {
        const kindA = String(getAttributeKind(a));
        const kindB = String(getAttributeKind(b));
        const pa = KIND_SORT_ORDER[kindA] ?? 1000;
        const pb = KIND_SORT_ORDER[kindB] ?? 1000;
        if (pa !== pb) return pa - pb;
        const defA = a?.attributeDefinition ?? a?.AttributeDefinition ?? "";
        const defB = b?.attributeDefinition ?? b?.AttributeDefinition ?? "";
        return String(defA).localeCompare(String(defB), "tr");
    });
};

export const attributeHasSelectableItems = (attr) =>
    getAttributeItemList(attr).some((it) => {
        const id = getItemIdFromAttrItem(it);
        return id != null && id !== "" && String(id).toLowerCase() !== EMPTY_UUID.toLowerCase();
    });

export const getUsableAttributeList = (detail) =>
    sortAttributesByKind(getAttributeList(detail).filter(attributeHasSelectableItems));

export const getUsableAttributeListLength = (detail) => getUsableAttributeList(detail).length;

export const normalizeDetailForStepper = (detail) => {
    const usable = getUsableAttributeList(detail);
    return {
        ...detail,
        specialtyAttributeList: usable,
        SpecialtyAttributeList: usable,
        specialtyCategoryAttributeList: usable,
        SpecialtyCategoryAttributeList: usable,
    };
};

const hasSpecialtyScoped = (detail) => {
    const explicit =
        detail?.hasSpecialtyScopedQuestions ??
        detail?.HasSpecialtyScopedQuestions ??
        detail?.hasBranchScopedQuestions ??
        detail?.HasBranchScopedQuestions;
    if (explicit != null) return Boolean(explicit);
    return Boolean(
        detail?.titlesFollowSpecialty ??
            detail?.TitlesFollowSpecialty ??
            detail?.isDoctorService ??
            detail?.IsDoctorService
    );
};

export const filterDetailBySpecialty = (detail, specialtyIdOrBranchId) => {
    if (!detail || !specialtyIdOrBranchId) return detail;
    const list = getAttributeList(detail);
    if (!list.length) return detail;
    const getAttrSpecialtyId = (attr) =>
        attr?.specialtyId ?? attr?.SpecialtyId ?? attr?.branchId ?? attr?.BranchId;
    const hasSpecialtyMetadata = list.some((attr) => getAttrSpecialtyId(attr) != null);
    if (!hasSpecialtyMetadata) return detail;

    const specialtyScoped = hasSpecialtyScoped(detail);

    const filtered = list.filter((attr) => {
        const attrSpecialtyId = getAttrSpecialtyId(attr);
        const matches =
            attrSpecialtyId != null &&
            String(attrSpecialtyId).toLowerCase() === String(specialtyIdOrBranchId).toLowerCase();

        if (specialtyScoped) {
            return matches;
        }
        if (!attrSpecialtyId) return true;
        return matches;
    });
    return {
        ...detail,
        specialtyAttributeList: filtered,
        SpecialtyAttributeList: filtered,
        specialtyCategoryAttributeList: filtered,
        SpecialtyCategoryAttributeList: filtered,
    };
};

/** @deprecated use filterDetailBySpecialty */
export const filterDetailByBranch = filterDetailBySpecialty;

/**
 * Uzmanlık seçili akış: filtre → kind sırası → kullanılabilir sorular.
 * Doküman: sayı sınırı yok (eski max-3 cap kaldırıldı).
 */
export const toBranchScopedStepperDetail = (detail, specialtyIdOrBranchId) => {
    if (!detail) return null;
    const filtered = filterDetailBySpecialty(detail, specialtyIdOrBranchId);
    return normalizeDetailForStepper(filtered);
};

export const getTitleList = (detail) => {
    const list =
        detail?.titleList ??
        detail?.TitleList ??
        detail?.filters?.titleList ??
        detail?.Filters?.TitleList;
    return Array.isArray(list) ? list : [];
};

export const getSpecialtyList = (detail) => {
    const list =
        detail?.specialtyList ??
        detail?.SpecialtyList ??
        detail?.branchList ??
        detail?.BranchList ??
        detail?.filters?.specialtyList ??
        detail?.Filters?.SpecialtyList;
    return Array.isArray(list) ? list : [];
};

export const getSpecialtyTitleMap = (detail) =>
    detail?.specialtyTitleMap ??
    detail?.SpecialtyTitleMap ??
    detail?.branchTitleMap ??
    detail?.BranchTitleMap ??
    null;

export const getTitlesFollowSpecialty = (detail) => {
    const explicit =
        detail?.titlesFollowSpecialty ?? detail?.TitlesFollowSpecialty;
    if (explicit != null) return Boolean(explicit);
    const map = getSpecialtyTitleMap(detail);
    return Boolean(map && typeof map === "object" && Object.keys(map).length > 0);
};

/** Ünvan / uzmanlık dropdown'ları için: en az biri eksik veya boş mu? */
export const detailNeedsTitleOrBranchLists = (detail) => {
    if (!detail) return true;
    const tOk = getTitleList(detail).length > 0;
    const bOk = getSpecialtyList(detail).length > 0;
    return !tOk || !bOk;
};

/**
 * scoped detayda eksik kalan titleList / specialtyList alanlarını source (tam GET) ile doldurur.
 * Attribute listesini değiştirmez.
 */
export const mergeTitleAndBranchLists = (target, source) => {
    if (!target || !source) return target;
    const srcTitles = getTitleList(source);
    const srcSpecialties = getSpecialtyList(source);
    const srcMap = getSpecialtyTitleMap(source);
    let out = { ...target };
    if (getTitleList(out).length === 0 && srcTitles.length) {
        out = { ...out, titleList: srcTitles, TitleList: srcTitles };
    }
    if (getSpecialtyList(out).length === 0 && srcSpecialties.length) {
        out = {
            ...out,
            specialtyList: srcSpecialties,
            SpecialtyList: srcSpecialties,
            branchList: srcSpecialties,
            BranchList: srcSpecialties,
        };
    }
    if (!getSpecialtyTitleMap(out) && srcMap) {
        out = {
            ...out,
            specialtyTitleMap: srcMap,
            SpecialtyTitleMap: srcMap,
            branchTitleMap: srcMap,
            BranchTitleMap: srcMap,
        };
    }
    if (source.titlesFollowSpecialty != null || source.TitlesFollowSpecialty != null) {
        const follow = getTitlesFollowSpecialty(source);
        out = { ...out, titlesFollowSpecialty: follow, TitlesFollowSpecialty: follow };
    }
    return out;
};

/** Tam kategori cevabından yalnızca ünvan/uzmanlık listelerini scoped sonuca yapıştırır. */
export const enrichScopedDetailWithFullLists = (scopedDetail, fullDetail) =>
    mergeTitleAndBranchLists(scopedDetail, fullDetail);

const isValidAttrId = (id) =>
    id != null && id !== "" && String(id).toLowerCase() !== EMPTY_UUID.toLowerCase();

/** Profil/API satırından seçilen attribute item id'sini çıkar (yeni + eski alan). */
export const getSelectedAttributeItemId = (item) => {
    const top =
        item?.specialtyAttributeItemId ??
        item?.SpecialtyAttributeItemId ??
        item?.specialtyCategoryAttributeItemId ??
        item?.SpecialtyCategoryAttributeItemId;
    const nested =
        item?.specialtyAttributeItem?.id ??
        item?.specialtyCategoryAttributeItem?.id ??
        item?.id;
    if (isValidAttrId(top)) return top;
    return nested;
};

export const getDoctorAttributeItems = (profileOrValues) => {
    const list =
        profileOrValues?.doctorSpecialtyAttributeItems ??
        profileOrValues?.DoctorSpecialtyAttributeItems ??
        profileOrValues?.doctorSpecialtyCategoryAttributeItems ??
        profileOrValues?.DoctorSpecialtyCategoryAttributeItems;
    return Array.isArray(list) ? list : [];
};

/** Yazma için kanonik satırlar: doctorSpecialtyAttributeItems[].specialtyAttributeItemId */
export const toDoctorSpecialtyAttributeWriteItems = (items) =>
    (Array.isArray(items) ? items : [])
        .map((item) => ({
            specialtyAttributeItemId: getSelectedAttributeItemId(item),
        }))
        .filter((item) => isValidAttrId(item.specialtyAttributeItemId));

export const isPracticeAreaAttribute = (attr) => {
    const kind = String(getAttributeKind(attr));
    if (kind === "PracticeArea") return true;
    const def = String(attr?.attributeDefinition ?? attr?.AttributeDefinition ?? "").toLowerCase();
    return def.includes("hastalık") || def.includes("çalışma") || def.includes("practice");
};

/** Onay kuralı UX: PracticeArea kind'ından en az 1 seçim. */
export const hasAtLeastOnePracticeAreaSelection = (detail, selectedItems) => {
    const practiceAttrs = getUsableAttributeList(detail).filter(isPracticeAreaAttribute);
    if (!practiceAttrs.length) return true;
    const practiceItemIds = new Set(
        practiceAttrs.flatMap((attr) =>
            getAttributeItemList(attr)
                .map((it) => getItemIdFromAttrItem(it))
                .filter(isValidAttrId)
                .map((id) => String(id).toLowerCase())
        )
    );
    if (!practiceItemIds.size) return true;
    return toDoctorSpecialtyAttributeWriteItems(selectedItems).some((row) =>
        practiceItemIds.has(String(row.specialtyAttributeItemId).toLowerCase())
    );
};
